import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import {
  BarChart, Bar, PieChart, Pie, Cell, Tooltip, ResponsiveContainer, XAxis, YAxis, LabelList
} from 'recharts';
import { CheckCircle, Clock, XCircle, AlertCircle, TrendingUp, RefreshCw, FileText } from 'lucide-react';

const COLORS = ['#f59e0b', '#F87171'];
const AUTO_REFRESH_INTERVAL = 60000;

const BCMAssessmentDashboardPage = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState({
    phaseScores: [],
    answerStats: { yes: 0, no: 0 },
    overallScore: null,
    overview: { phases: 0, focusAreas: 0, questions: 0 },
    recentActivities: [],
    deadlines: [],
    notifications: [],
    userProfile: { name: '', department: '', email: '' },
    assessments: [],
    yourAssessments: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      setError(null);
      const [dashboardRes, bcmPhasesRes, overdueRes] = await Promise.all([
        api.get('maturity/analytics/bcm-dashboard/'),
        api.get('maturity/bcm/phases/?all=true'),
        api.get('maturity/assessments/dashboard/bcm/'),
      ]);

      const {
        phase_scores,
        total_yes,
        total_no,
        maturity_level,
        maturity_label,
        overview,
        recent_activities,
        deadlines,
        notifications,
        user_profile,
      } = dashboardRes.data;

      const bcmPhases = Array.isArray(bcmPhasesRes.data) ? bcmPhasesRes.data : [];
      const bcmPhaseMap = {};
      phase_scores?.forEach((p) => {
        bcmPhaseMap[p.phase_id] = p;
      });

      const mergedPhaseScores = bcmPhases.map((phase) => ({
        phase_id: phase.id,
        phase_name: phase.title || `Phase ${phase.id}`,
        phase_code: phase.code || `P${phase.id}`,
        score: bcmPhaseMap[phase.id]?.score ?? 0,
      }));

      const bcmDeadlines = Array.isArray(deadlines)
        ? deadlines.filter((item) => item.name?.toLowerCase().includes('bcm'))
        : [];

      const assessmentsData = Array.isArray(overdueRes.data.assessments)
        ? overdueRes.data.assessments.map((a) => ({
            ...a,
            is_overdue: a.deadline ? new Date(a.deadline) < new Date() : false,
          }))
        : [];

      const yourAssessments = Array.isArray(dashboardRes.data.your_assessments)
        ? dashboardRes.data.your_assessments
        : [];

      setReport({
        phaseScores: mergedPhaseScores,
        answerStats: { yes: total_yes || 0, no: total_no || 0 },
        overallScore: maturity_level ?? null,
        maturityLabel: maturity_label ?? null,
        overview: overview || { phases: 0, focusAreas: 0, questions: 0 },
        recentActivities: Array.isArray(recent_activities) ? recent_activities : [],
        deadlines: bcmDeadlines,
        notifications: Array.isArray(notifications) ? notifications : [],
        userProfile: user_profile || { name: '', department: '', email: '' },
        assessments: assessmentsData,
        yourAssessments,
      });

      setLastRefreshed(new Date());
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Failed to load dashboard. Please try again.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();

    const intervalId = setInterval(() => {
      if (isAutoRefreshEnabled) {
        fetchDashboard();
      }
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAutoRefreshEnabled, fetchDashboard]);

  const getMaturityColor = (score) => {
    if (score === 0) return 'text-gray-500';
    if (score === 1) return 'text-red-500';
    if (score === 2) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getBadgeColor = (status) => {
    if (status?.toLowerCase().includes('submitted')) return 'bg-blue-100 text-blue-800';
    if (status?.toLowerCase().includes('overdue')) return 'bg-red-100 text-red-800';
    return 'bg-green-100 text-green-800';
  };

  const handleManualRefresh = () => {
    setLoading(true);
    fetchDashboard();
  };

  const handleToggleAutoRefresh = () => {
    setIsAutoRefreshEnabled(!isAutoRefreshEnabled);
  };

  const assessmentCTA = useMemo(() => {
    const items = report.yourAssessments || [];

    if (!items.length) {
      return {
        label: 'Start Assessment',
        onClick: () => navigate('/bcm/assessment'),
        icon: <TrendingUp size={18} />,
      };
    }

    const hasActive = items.some((a) => !a.status?.toLowerCase().includes('submitted'));
    const hasSubmitted = items.some((a) => a.status?.toLowerCase().includes('submitted'));

    if (hasActive) {
      return {
        label: 'Continue Assessment',
        onClick: () => navigate('/bcm/assessment'),
        icon: <TrendingUp size={18} />,
      };
    }

    if (hasSubmitted) {
      return {
        label: 'View Report',
        onClick: () => navigate('/bcm/report'),
        icon: <FileText size={18} />,
      };
    }

    return {
      label: 'Start Assessment',
      onClick: () => navigate('/bcm/assessment'),
      icon: <TrendingUp size={18} />,
    };
  }, [report.yourAssessments, navigate]);

  if (loading && !report.userProfile?.name && !report.userProfile?.email) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-amber-700 font-medium">Loading BCM Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !report.userProfile?.name && !report.userProfile?.email) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <button
            onClick={handleManualRefresh}
            className="bg-amber-600 text-white px-6 py-2 rounded hover:bg-amber-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { phaseScores, answerStats, overallScore, overview, userProfile, recentActivities, deadlines, notifications } = report;
  const totalResponses = answerStats.yes + answerStats.no;
  const completionPercentage = totalResponses > 0 ? Math.round((answerStats.yes / totalResponses) * 100) : 0;

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen px-4 sm:px-6 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                Welcome back, <span className="text-amber-600">{userProfile.name || 'User'}</span>!
              </h1>
              <p className="text-gray-600 mt-2">
                Department: <span className="font-medium">{userProfile.department || 'N/A'}</span>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="text-right">
                <p className="text-sm text-gray-500">Last updated: {lastRefreshed.toLocaleTimeString()}</p>
                <p className="text-xs text-gray-400 mt-1">Auto-refresh: {isAutoRefreshEnabled ? '🟢 On' : '🔴 Off'}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleManualRefresh}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition shadow-md"
                  title="Manually refresh dashboard"
                >
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                  Refresh
                </button>
                <button
                  onClick={handleToggleAutoRefresh}
                  className={`px-4 py-2 rounded-lg font-medium transition shadow-md ${
                    isAutoRefreshEnabled
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-400 hover:bg-gray-500 text-white'
                  }`}
                  title={isAutoRefreshEnabled ? 'Disable auto-refresh' : 'Enable auto-refresh'}
                >
                  {isAutoRefreshEnabled ? 'Auto On' : 'Auto Off'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
              <p className="text-red-700 text-sm mt-1">Will retry automatically in {AUTO_REFRESH_INTERVAL / 1000} seconds</p>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={assessmentCTA.onClick}
            className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-lg font-medium transition shadow-md hover:shadow-lg flex items-center justify-center gap-2 flex-1 sm:flex-none"
          >
            {assessmentCTA.icon}
            {assessmentCTA.label}
          </button>
          <button
            onClick={() => navigate('/bcm/report')}
            className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 rounded-lg font-medium transition shadow-md hover:shadow-lg flex items-center justify-center gap-2 flex-1 sm:flex-none"
          >
            📊 View Report
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[
            { label: 'Phases', value: overview.phases, icon: '📊' },
            { label: 'Focus Areas', value: overview.focusAreas, icon: '🎯' },
            { label: 'Questions', value: overview.questions, icon: '❓' },
          ].map((item, idx) => (
            <div key={idx} className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">{item.label}</p>
                  <p className="text-4xl font-bold text-amber-700 mt-2">{item.value}</p>
                </div>
                <span className="text-4xl">{item.icon}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={`bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-xl shadow-lg border-2 border-amber-200 mb-10`}>
          <div className="text-center">
            <p className="text-gray-600 font-medium mb-2">Overall Maturity Score</p>
            <p className={`text-6xl font-bold ${getMaturityColor(overallScore)} mb-4`}>
              {overallScore ?? 'N/A'}
            </p>
            {report.maturityLabel && (
              <p className="text-lg text-gray-700 font-semibold">{report.maturityLabel}</p>
            )}
            {totalResponses > 0 && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <div className="flex-1 max-w-xs bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${completionPercentage}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-700">{completionPercentage}% Complete</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              📈 Phase Maturity Levels
            </h2>
            {phaseScores.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={phaseScores
                      .sort((a, b) => {
                        const numA = parseInt(a.phase_code.replace(/\D/g, ''));
                        const numB = parseInt(b.phase_code.replace(/\D/g, ''));
                        return numA - numB;
                      })
                      .map((p) => {
                        let level = 0;
                        if (p.score === 0) level = 0;
                        else if (p.score < 0.2) level = 1;
                        else if (p.score <= 0.5) level = 2;
                        else level = 3;

                        return {
                          name: p.phase_code,
                          fullName: p.phase_name,
                          level,
                          score: p.score
                        };
                      })}
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                  >
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      domain={[0, 3]}
                      ticks={[0, 1, 2, 3]}
                      label={{ value: 'Maturity Level', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip
                      formatter={(value) => {
                        const labels = {
                          0: '0 - Non-Existent',
                          1: '1 - Initial',
                          2: '2 - Repeatable',
                          3: '3 - Defined'
                        };
                        return labels[value] || value;
                      }}
                      labelFormatter={(label) => {
                        const phase = phaseScores.find((p) => p.phase_code === label);
                        return phase ? `${phase.phase_code}: ${phase.phase_name}` : label;
                      }}
                      contentStyle={{
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '13px'
                      }}
                    />
                    <Bar dataKey="level" fill="#f59e0b" radius={[8, 8, 0, 0]}>
                      <LabelList
                        dataKey="level"
                        position="insideTop"
                        formatter={(value) => value}
                        style={{ fill: 'white', fontSize: 14, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {phaseScores
                    .sort((a, b) => {
                      const numA = parseInt(a.phase_code.replace(/\D/g, ''));
                      const numB = parseInt(b.phase_code.replace(/\D/g, ''));
                      return numA - numB;
                    })
                    .map((phase, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm font-bold text-amber-600">{phase.phase_code}</p>
                        <p className="text-xs text-gray-600 mt-1 leading-tight">{phase.phase_name}</p>
                      </div>
                    ))}
                </div>
              </>
            ) : (
              <div className="h-80 flex items-center justify-center text-gray-400">
                <p>No phase data available</p>
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              📊 Response Distribution
            </h2>
            {totalResponses > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Compliant', value: answerStats.yes },
                        { name: 'Non-Compliant', value: answerStats.no },
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, value }) => `${name}: ${value}`}
                      dataKey="value"
                    >
                      {COLORS.map((color, index) => <Cell key={index} fill={color} />)}
                    </Pie>
                    <Tooltip
                      formatter={(value) => `${value} responses`}
                      contentStyle={{
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '12px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Compliant</p>
                    <p className="text-2xl font-bold text-amber-600">{answerStats.yes}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Non-Compliant</p>
                    <p className="text-2xl font-bold text-red-500">{answerStats.no}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">
                <p>No response data available</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {[
            { title: '🔄 Recent Activity', data: recentActivities, type: 'activity' },
            { title: '⏰ Upcoming Deadlines', data: deadlines, type: 'deadline' },
            { title: '🔔 Notifications', data: notifications, type: 'notification' },
          ].map(({ title, data, type }) => (
            <div key={title} className="bg-white p-6 rounded-xl shadow border border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{title}</h2>
              {data.length > 0 ? (
                <ul className="space-y-3 max-h-64 overflow-y-auto">
                  {data.slice(0, 5).map((item, i) => {
                    if (type === 'activity') {
                      return (
                        <li key={i} className="pb-3 border-b border-gray-200 last:border-b-0">
                          <p className="font-semibold text-gray-900 text-sm">{item.assessment_name}</p>
                          <p className={`text-xs font-medium mt-1 ${
                            item.status === 'Completed' ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            {item.status}
                          </p>
                        </li>
                      );
                    }

                    if (type === 'deadline') {
                      const deadlineDate = item.deadline ? new Date(item.deadline).toLocaleDateString() : 'N/A';
                      return (
                        <li key={i} className="pb-3 border-b border-gray-200 last:border-b-0">
                          <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                          <p className="text-xs text-gray-600 mt-1">Due: {deadlineDate}</p>
                          <span className={`inline-block mt-2 text-xs font-bold px-2 py-1 rounded ${
                            item.status === 'Overdue' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {item.status}
                          </span>
                        </li>
                      );
                    }

                    if (type === 'notification') {
                      return (
                        <li key={i} className="pb-3 border-b border-gray-200 last:border-b-0">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-bold mb-2 ${
                            item.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                            item.type === 'success' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {item.type?.toUpperCase()}
                          </span>
                          <p className="text-xs text-gray-700 mt-1">{item.message}</p>
                        </li>
                      );
                    }

                    return <li key={i}>{item}</li>;
                  })}
                </ul>
              ) : (
                <p className="text-gray-400 text-sm">No {title.toLowerCase()}</p>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white p-6 rounded-xl shadow border border-gray-200 mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-6">📋 Your Assessments</h2>
          {report.yourAssessments?.length > 0 ? (
            <div className="space-y-3">
              {report.yourAssessments.map((a) => {
                const deadlineText = a.deadline ? new Date(a.deadline).toLocaleDateString() : 'N/A';
                const badgeClass = getBadgeColor(a.status);

                return (
                  <div key={a.assessment_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition border border-gray-200">
                    <div className="flex-1 mb-3 sm:mb-0">
                      <p className="font-semibold text-gray-900">{a.name}</p>
                      <p className="text-sm text-gray-600 mt-1">Due: <span className="font-medium">{deadlineText}</span></p>
                    </div>
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${badgeClass} whitespace-nowrap`}>
                      {a.status?.toLowerCase().includes('submitted') && <CheckCircle size={14} />}
                      {a.status?.toLowerCase().includes('on time') && <Clock size={14} />}
                      {a.status?.toLowerCase().includes('overdue') && <XCircle size={14} />}
                      {a.status || 'Pending'}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400">No assessments available</p>
          )}
        </div>

        <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-6">👤 User Profile</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="pb-4 sm:pb-0 sm:border-r border-gray-200">
              <p className="text-sm text-gray-600 font-medium">Name</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">{userProfile.name || 'N/A'}</p>
            </div>
            <div className="pb-4 sm:pb-0 sm:border-r border-gray-200">
              <p className="text-sm text-gray-600 font-medium">Department</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">{userProfile.department || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">Email</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">{userProfile.email || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BCMAssessmentDashboardPage;